import React from 'react';
import {
  FileText,
  AlertTriangle,
  DollarSign,
  Shield,
  CheckCircle,
  CreditCard,
  Globe,
  BarChart3,
  ChevronDown,
  ChevronRight,
  FileCheck,
  UserCircle,
  LineChart,
  Wallet,
  Briefcase,
  ShieldAlert,
} from 'lucide-react';

const PLATFORM_SECTIONS = [
  {
    id: 'execReport',
    name: 'Executive Reports',
    icon: LineChart,
    color: 'indigo',
    reports: [
      { id: 'executive-kpi-snapshot', name: 'Generate Executive KPI Report' },
      { id: 'saved-reports', name: 'Saved Reports' },
      { id: 'compare-reports', name: 'Compare Reports' },
    ],
  },
  {
    id: 'financeReport',
    name: 'Finance Reports',
    icon: Wallet,
    color: 'emerald',
    reports: [
      { id: 'finance-view-summary', name: 'Finance View Summary' },
      { id: 'vs-ehr-variance', name: 'vs EHR Variance Report' },
      { id: 'capture-unmatched', name: 'Capture & Unmatched Lists' },
      { id: 'dollars-at-risk', name: 'Dollars at Risk Report' },
    ],
  },
];

const OPERATIONAL_SECTIONS = [
  {
    id: 'denial',
    name: 'Denials & Appeals',
    icon: AlertTriangle,
    color: 'red',
    reports: [
      { id: 'denial-summary', name: 'Denial Summary Report' },
      { id: 'appeal-analysis', name: 'Appeal Analysis Report' },
      { id: 'payer-performance', name: 'Payer Performance Report' },
      { id: 'financial-impact', name: 'Financial Impact Report' },
      { id: 'provider-performance', name: 'Provider Performance Report' },
      { id: 'compliance-report', name: 'Compliance Report' },
    ],
  },
  {
    id: 'claims',
    name: 'Claims Processing',
    icon: FileText,
    color: 'blue',
    reports: [
      { id: 'financial-summary', name: 'Financial Summary Report' },
      { id: 'rejection-analysis', name: 'Rejection Analysis Report' },
      { id: 'performance-metrics', name: 'Performance Metrics Report' },
      { id: 'clean-claim-rate', name: 'Clean Claim Rate Report' },
      { id: 'submission-performance', name: 'Submission Performance Report' },
      { id: 'error-patterns', name: 'Error Patterns Report' },
    ],
  },
  {
    id: 'arPosting',
    name: 'AR Posting',
    icon: CreditCard,
    color: 'green',
    reports: [
      { id: 'payment-analysis', name: 'Cash Posting Report' },
      { id: 'auto-post', name: 'Auto-Post Report' },
      { id: 'reconciliation-analysis', name: 'Deposit Reconciliation Report' },
      { id: 'adjustment-analysis', name: 'Adjustment Analysis Report' },
      { id: 'deposit-matching', name: 'Deposit Matching Report' },
      { id: 'underpayments', name: 'Underpayment Report' },
    ],
  },
  {
    id: 'charges',
    name: 'Charge Management',
    icon: DollarSign,
    color: 'purple',
    reports: [
      { id: 'charge-metrics', name: 'Charge Metrics Report' },
      { id: 'missing-encounters', name: 'Missing Encounters Report' },
      { id: 'charge-lag', name: 'Charge Lag Report' },
      { id: 'revenue-summary', name: 'Revenue Summary Report' },
      { id: 'code-review', name: 'Code Review Report' },
    ],
  },
  {
    id: 'priorAuth',
    name: 'Prior Authorization',
    icon: CheckCircle,
    color: 'yellow',
    reports: [
      { id: 'authorization-summary', name: 'Authorization Summary Report' },
      { id: 'provider-performance', name: 'Provider Performance Report' },
      { id: 'patient-demographics', name: 'Patient Demographics Report' },
      { id: 'audit-trail', name: 'Audit Trail Report' },
      { id: 'edi-transaction-log', name: 'EDI Transaction Log' },
    ],
  },
  {
    id: 'eligibility',
    name: 'Insurance Verification',
    icon: Shield,
    color: 'indigo',
    reports: [
      { id: 'verification-summary', name: 'Verification Summary Report' },
      { id: 'benefits-analysis', name: 'Benefits Analysis Report' },
      { id: 'patient-responsibility', name: 'Patient Responsibility Report' },
      { id: 'coverage-status', name: 'Coverage Status Report' },
    ],
  },
  {
    id: 'remittanceAdvice',
    name: 'Remittance Advice',
    icon: BarChart3,
    color: 'orange',
    reports: [
      { id: 'volume', name: 'RA Volume & Trends Report' },
      { id: 'status', name: 'Payment Status Report' },
      { id: 'financial', name: 'Financial Summary Report' },
      { id: 'processing', name: 'Processing Metrics Report' },
      { id: 'era', name: 'ERA Analytics Report' },
      { id: 'payment-velocity', name: 'Payment Velocity Report' },
      { id: 'reversal-rate', name: 'Reversal Rate Report' },
      { id: 'payer-drift', name: 'Payer Drift Report' },
    ],
  },
  {
    id: 'ediPlatform',
    name: 'EDI Platform',
    icon: Globe,
    color: 'gray',
    reports: [
      { id: 'transaction-log', name: 'Transaction Log Report' },
      { id: 'success-rate', name: 'Success Rate Report' },
      { id: 'error-analysis', name: 'Error Analysis Report' },
    ],
  },
  {
    id: 'contractManagement',
    name: 'Contract Management',
    icon: FileCheck,
    color: 'teal',
    reports: [
      { id: 'contract-performance', name: 'Contract Performance Report' },
      { id: 'payer-agreement-analysis', name: 'Payer Agreement Analysis' },
      { id: 'fee-schedule-comparison', name: 'Fee Schedule Comparison' },
      { id: 'contract-compliance', name: 'Contract Compliance Report' },
      { id: 'revenue-impact', name: 'Revenue Impact Report' },
    ],
  },
  {
    id: 'patientBilling',
    name: 'Patient Billing',
    icon: UserCircle,
    color: 'pink',
    reports: [
      { id: 'billing-summary', name: 'Billing Summary Report' },
      { id: 'payment-collection', name: 'Payment Collection Report' },
      { id: 'outstanding-balance', name: 'Outstanding Balance Report' },
      { id: 'payment-plan-analysis', name: 'Payment Plan Analysis' },
      { id: 'patient-responsibility', name: 'Patient Responsibility Report' },
    ],
  },
  {
    id: 'compliance',
    name: 'RCM Compliance',
    icon: ShieldAlert,
    color: 'slate',
    reports: [
      { id: 'executive-compliance', name: 'Executive Compliance Summary' },
      { id: 'risk-register', name: 'Risk Register & Hotspots' },
      { id: 'audit-workpack', name: 'Audit Workpack' },
    ],
  },
];

const OPERATIONAL_IDS = new Set(OPERATIONAL_SECTIONS.map((s) => s.id));

const COLOR_CLASSES = {
  red: { bg: 'bg-red-100', text: 'text-red-600', hover: 'hover:bg-red-50' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'hover:bg-blue-50' },
  green: { bg: 'bg-green-100', text: 'text-green-600', hover: 'hover:bg-green-50' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-50' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', hover: 'hover:bg-yellow-50' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', hover: 'hover:bg-indigo-50' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600', hover: 'hover:bg-gray-50' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600', hover: 'hover:bg-teal-50' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600', hover: 'hover:bg-pink-50' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', hover: 'hover:bg-emerald-50' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-700', hover: 'hover:bg-slate-50' },
};

const ReportingSidebar = ({ activeService, activeReport, onServiceChange, onReportChange }) => {
  const [expandedSections, setExpandedSections] = React.useState({
    execReport: true,
    financeReport: true,
    operationalReports: true,
    denial: false,
    claims: false,
    arPosting: false,
    charges: false,
    priorAuth: false,
    eligibility: false,
    remittanceAdvice: false,
    ediPlatform: false,
    contractManagement: false,
    patientBilling: false,
    compliance: false,
  });

  React.useEffect(() => {
    if (activeService && OPERATIONAL_IDS.has(activeService)) {
      setExpandedSections((prev) => ({
        ...prev,
        operationalReports: true,
        [activeService]: true,
      }));
    } else if (activeService === 'execReport' || activeService === 'financeReport') {
      setExpandedSections((prev) => ({
        ...prev,
        [activeService]: true,
      }));
    }
  }, [activeService]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const getColorClasses = (color) => COLOR_CLASSES[color] || COLOR_CLASSES.gray;

  const renderSection = (section, { nested = false } = {}) => {
    const colorClasses = getColorClasses(section.color);
    const isExpanded = expandedSections[section.id];
    const isActive = activeService === section.id;

    return (
      <div key={section.id} className={nested ? 'mb-1' : 'mb-2'}>
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          className={`w-full flex items-center justify-between px-2 py-2 text-left rounded-md transition-colors ${
            isActive ? `${colorClasses.bg} ${colorClasses.text} font-semibold` : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <section.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? colorClasses.text : 'text-gray-500'}`} />
            <span className="text-sm truncate text-left">{section.name}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          )}
        </button>

        {isExpanded && (
          <div className={`${nested ? 'ml-5' : 'ml-7'} mt-1 space-y-1`}>
            {section.reports.map((report) => {
              const isReportActive = activeService === section.id && activeReport === report.id;
              return (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => {
                    onServiceChange(section.id);
                    onReportChange(report.id);
                  }}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors ${
                    isReportActive
                      ? `${colorClasses.bg} ${colorClasses.text} font-medium`
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {report.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const operationalExpanded = expandedSections.operationalReports;
  const operationalActive = activeService && OPERATIONAL_IDS.has(activeService);

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 text-blue-600 mr-2" />
          Reports
        </h2>
        <p className="text-xs text-gray-500 mt-1">Executive, Finance, and Operational</p>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        <button
          type="button"
          onClick={() => {
            onServiceChange(null);
            onReportChange(null);
          }}
          className={`w-full flex items-center justify-between px-2 py-2 text-sm font-semibold rounded-md transition-colors mb-2 ${
            !activeService
              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center space-x-2">
            <FileText className={`w-5 h-5 ${!activeService ? 'text-blue-600' : 'text-gray-500'}`} />
            <span>All Reports</span>
          </div>
        </button>

        {PLATFORM_SECTIONS.map((section) => renderSection(section))}

        <div className="mt-3 mb-1 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => toggleSection('operationalReports')}
            className={`w-full flex items-center justify-between px-2 py-2 text-left rounded-md transition-colors ${
              operationalActive
                ? 'bg-slate-100 text-slate-800 font-semibold'
                : 'text-gray-800 hover:bg-gray-50 font-semibold'
            }`}
          >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <Briefcase className={`w-5 h-5 flex-shrink-0 ${operationalActive ? 'text-slate-700' : 'text-gray-500'}`} />
              <span className="text-sm truncate text-left">Operational Reports</span>
            </div>
            {operationalExpanded ? (
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            )}
          </button>

          {operationalExpanded && (
            <div className="ml-2 mt-1 pl-2 border-l border-gray-200">
              {OPERATIONAL_SECTIONS.map((section) => renderSection(section, { nested: true }))}
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default ReportingSidebar;
